// //client/src/pages/play/Quiz.tsx

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Progress,
  Text,
  VStack,
  useToast,
  Center,
  Spinner,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import QuestionCard from "../../components/QuestionCard";
import { quizQuestions } from "../../data/quizData";
import { QuizAnswer, calculateScores } from "../../utils/scoring";

const MotionBox = motion(Box);
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5043/api";

export default function Quiz() {
const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  // Add responsive values
  const containerMaxW = useBreakpointValue({
    base: "100%",
    md: "container.md",
    lg: "container.lg",
  });

  // Enhanced responsive values
  const spacing = useBreakpointValue({ base: 3, md: 4, lg: 5 });
  const buttonSize = useBreakpointValue({ base: "sm", md: "md" });
  const containerPadding = useBreakpointValue({ base: 2, md: 4 });
  const progressTextSize = useBreakpointValue({ base: "sm", md: "md" });

  
  // Redirect if no userId
  useEffect(() => {
    if (!userId) {
      navigate("/play");
    }
  }, [userId, navigate]);

  const currentQuestion = quizQuestions[currentQuestionIndex];
  const progress = ((currentQuestionIndex + 1) / quizQuestions.length) * 100;

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestionIndex] = {
      questionId: currentQuestion.id,
      answer: answerIndex,
    };
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestionIndex < quizQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      handleSubmit();
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (answers.length !== quizQuestions.length) {
      toast({
        title: "Incomplete Quiz",
        description: "Please answer all questions before submitting.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Calculate scores
      const scores = calculateScores(answers);

      // Format the data to match backend expectations
      const formattedAnswers = answers.map((answer) => ({
        questionId: answer.questionId,
        answer: answer.answer,
      }));

      // Convert scores to backend format
      const formattedScores = scores.map((score) => ({
        archetype: score.archetype,
        value: score.value,
      }));

      // Submit answers to backend
      await axios.post(`${API_URL}/user/${userId}/quiz`, {
        answers: formattedAnswers,
        scores: formattedScores,
      });

      // Navigate to results
      navigate(`/play/end?userId=${userId}`);
    } catch (error: any) {
      console.error("Error submitting quiz:", error);
      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Failed to submit quiz. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!userId) {
    return null;
  }

  return (
    <Flex
      minH="100vh"
      w="100%"
      maxW="100vw"
      align="center"
      justify="center"
      position="relative"
      overflowX="hidden" // Prevent horizontal scroll
      px={containerPadding}
      py={4}
    >
      <VStack
        spacing={spacing}
        w="full"
        maxW={{ base: "95%", sm: "90%", md: "85%", lg: "32rem" }}
        mx="auto"
        py={{ base: 4, md: 6 }}
        px={{ base: 3, md: 5 }}
        // bg="rgba(58, 56, 56, 0.7)"
        // borderRadius="20px"
        boxShadow="lg"
        // border="3px solid"
        // borderColor="purple.200"
        position="relative"
        zIndex={1}
        color="white"
         bg="rgba(0, 0, 0, 0)" // Semi-transparent dark overlay
        backdropFilter="blur(8px)"
        borderRadius="xl"
        border="1px solid"
        borderColor="purple.200"
      >
        <Box w="full" maxW="100%" px={2}>
          <Progress
            value={progress}
            size="sm"
            colorScheme="purple"
            borderRadius="full"
            mb={3}
            bg="blackAlpha.400"
          />
          <Text
            textAlign="center"
            fontSize={progressTextSize}
            fontWeight="bold"
            letterSpacing="tight"
          >
            Question {currentQuestionIndex + 1} of {quizQuestions.length}
          </Text>
        </Box>

        {/* <AnimatePresence mode="wait">
          <QuestionCard
            key={currentQuestion.id}
            question={currentQuestion}
            selectedAnswer={answers[currentQuestionIndex]?.answer ?? null}
            onAnswerSelect={handleAnswerSelect}
            isDisabled={isSubmitting}
            bg="rgba(58, 56, 56, 0.7)"
            color="white"
          />
        </AnimatePresence> */}
        <AnimatePresence mode="wait">
  <Box w="full" px={2}> {/* Added this Box wrapper */}
    <QuestionCard
      key={currentQuestion.id}
      question={currentQuestion}
      selectedAnswer={answers[currentQuestionIndex]?.answer ?? null}
      onAnswerSelect={handleAnswerSelect}
      isDisabled={isSubmitting}
      // bg="rgba(58, 56, 56, 0.7)"
      color="white"
      bg="rgba(0, 0, 0, 0)" // Semi-transparent dark overlay
       
    />
  </Box>
</AnimatePresence>

        <VStack spacing={3} w="full" px={2}>
          <Button
            size={buttonSize}
            colorScheme="purple"
            onClick={handleNext}
            w="full"
            isDisabled={!answers[currentQuestionIndex] || isSubmitting}
            px={{ base: 4, md: 6 }}
            py={{ base: 3, md: 4 }}
            fontSize={{ base: "sm", md: "md" }}
            fontWeight="bold"
            bg="purple.500"
            _hover={{
              bg: "purple.600",
              transform: "translateY(-2px)",
            }}
          >
            {currentQuestionIndex === quizQuestions.length - 1
              ? "Submit"
              : "Next Question"}
          </Button>

          {currentQuestionIndex > 0 && (
            <Button
              variant="outline"
              onClick={handlePrevious}
              w="full"
              isDisabled={isSubmitting}
              size={buttonSize}
              fontSize={{ base: "sm", md: "md" }}
              borderColor="purple.300"
              color="purple.100"
              _hover={{
                bg: "purple.900",
                borderColor: "purple.200",
              }}
            >
              Previous Question
            </Button>
          )}
        </VStack>
      </VStack>

      {isSubmitting && (
        <Center
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.800"
          zIndex="modal"
        >
          <VStack
            spacing={4}
            bg="blackAlpha.700"
            p={8}
            borderRadius="xl"
            boxShadow="2xl"
            border="1px solid"
            borderColor="whiteAlpha.200"
          >
            <Spinner size="xl" color="purple.300" />
            <Text
              color="white"
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="medium"
            >
              Processing your answers...
            </Text>
          </VStack>
        </Center>
      )}
    </Flex>
  );
}
